import { Marker, MarkerContent } from '@neo/primitives/marker'
import {
  MessageScroller,
  MessageScrollerButton,
  MessageScrollerContent,
  MessageScrollerItem,
  MessageScrollerViewport,
} from '@neo/primitives/message-scroller'
import { ReactComponent as VibeMascot } from '@shared/assets/icons/vibe-mascot.svg'
import { m } from 'framer-motion'
import { ArrowDown } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { DURATION, EASE_OUT, welcomeContainer, welcomeItem } from '../motion'
import { lastAiBubbleIds, useVibeAgentStore } from '../store'
import { AnimatedGradientText } from './animated-gradient-text'
import { VibeEntry } from './entry'
import { VibeEvalPanel } from './eval-panel'

/**
 * The scrolling transcript on shadcn MessageScroller: turn anchoring (user
 * messages and auto-answers settle near the viewport top), live-edge
 * auto-follow that releases when the reader scrolls up, `last-anchor` opening
 * position for rehydrated transcripts, content-visibility row skipping, and
 * the built-in jump-to-latest button (replaces the hand-rolled stick-to-bottom
 * hook + pill).
 */
// Staged wait phrases (Codex-informed): GPT-5 reasoning can sit silent for
// 15-20s before the first visible act — a static "Thinking…" reads as hung.
// Phases advance on a timer and reset whenever the indicator hides.
const THINKING_PHASES: ReadonlyArray<readonly [string, number]> = [
  ['Thinking…', 5000],
  ['Working through your flow…', 5000],
  ['Planning changes…', 7000],
  ['Still thinking, this one needs a moment…', 0],
]

const emptyStateContainer = welcomeContainer()

const useThinkingPhrase = (active: boolean): string => {
  const [phase, setPhase] = useState(0)
  useEffect(() => {
    if (!active) {
      setPhase(0)
      return
    }
    let cancelled = false
    let timer: ReturnType<typeof setTimeout>
    const schedule = (idx: number) => {
      const delay = THINKING_PHASES[idx]?.[1]
      if (!delay) return // last phase holds
      timer = setTimeout(() => {
        if (cancelled) return
        setPhase(idx + 1)
        schedule(idx + 1)
      }, delay)
    }
    schedule(0)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [active])
  return THINKING_PHASES[Math.min(phase, THINKING_PHASES.length - 1)]?.[0] ?? 'Thinking…'
}

export const VibeTranscriptList = () => {
  const transcript = useVibeAgentStore((state) => state.transcript)
  const runStatus = useVibeAgentStore((state) => state.runStatus)
  const gate = useVibeAgentStore((state) => state.gate)

  // Derived thinking indicator — ephemeral UI, never a transcript entry. Shown
  // ONLY in the gap between the user's send and the agent's FIRST visible act
  // of the turn (tool call, text, anything): once any agent row lands, the
  // tail stops being the user's message and the indicator stays gone —
  // reappearing between steps read as the agent stuttering.
  const tail = transcript[transcript.length - 1]
  const showThinking =
    (runStatus === 'sending' || runStatus === 'running') && !gate && tail?.role === 'user'
  const thinkingPhrase = useThinkingPhrase(showThinking)

  // Rows present at FIRST render (bulk rehydration) must not replay entrance
  // animations; only rows appended live afterwards animate in.
  const initialIdsRef = useRef<Set<string> | null>(null)
  if (initialIdsRef.current === null) {
    initialIdsRef.current = new Set(transcript.map((entry) => entry.id))
  }
  const initialIds = initialIdsRef.current
  const feedbackIds = useMemo(() => lastAiBubbleIds(transcript), [transcript])

  return (
    <div className="relative flex-1 min-h-0">
      <MessageScroller>
        <MessageScrollerViewport>
          <MessageScrollerContent className="gap-0 px-2 py-3">
            {transcript.map((entry) => (
              <MessageScrollerItem
                key={entry.id}
                messageId={entry.id}
                className={entry.pending ? 'opacity-70' : undefined}
              >
                <m.div
                  // Highest-frequency animation in the panel: fast ease-out,
                  // full transform string (GPU) — shorthands run on the main
                  // thread, which is busiest exactly when rows stream.
                  initial={
                    initialIds.has(entry.id) ? false : { opacity: 0, transform: 'translateY(8px)' }
                  }
                  animate={{ opacity: 1, transform: 'translateY(0px)' }}
                  transition={{ duration: DURATION.enter, ease: EASE_OUT }}
                >
                  <VibeEntry entry={entry} showFeedback={feedbackIds.has(entry.id)} />
                </m.div>
              </MessageScrollerItem>
            ))}

            {showThinking && (
              <MessageScrollerItem messageId="__thinking__" className="my-2">
                {/* Opacity-only, near-imperceptible tier: this appears every
                      turn, so any movement would read as lag. Exit stays
                      instant — the scroller Item is not a motion child. */}
                <m.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: DURATION.exitFast, ease: EASE_OUT }}
                >
                  <Marker role="status" data-testid="vibe-thinking-indicator">
                    <MarkerContent className="text-p-400">
                      <m.span
                        key={thinkingPhrase}
                        className="block"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: DURATION.minor, ease: EASE_OUT }}
                      >
                        <AnimatedGradientText>{thinkingPhrase}</AnimatedGradientText>
                      </m.span>
                    </MarkerContent>
                  </Marker>
                </m.div>
              </MessageScrollerItem>
            )}

            <div>
              <VibeEvalPanel />
            </div>

            {transcript.length === 0 && (
              <div className="flex flex-col items-center justify-center h-64">
                <m.div
                  className="text-center space-y-3"
                  variants={emptyStateContainer}
                  initial="hidden"
                  animate="visible"
                >
                  {/* Buddy's head above the greeting; joins the same stagger as the copy. */}
                  <m.div variants={welcomeItem} className="flex justify-center">
                    <VibeMascot className="pointer-events-none size-12" aria-hidden="true" />
                  </m.div>
                  <m.p variants={welcomeItem} className="text-secondary text-h3-400">
                    Start a conversation with{' '}
                    <AnimatedGradientText>Vibe Agent</AnimatedGradientText>
                  </m.p>
                  <m.p
                    variants={welcomeItem}
                    className="text-secondary/70 line-clamp-2 max-w-xs text-p-400"
                  >
                    Build a new flow agent from scratch, ask questions about your flow, or request
                    changes.
                  </m.p>
                </m.div>
              </div>
            )}
          </MessageScrollerContent>
        </MessageScrollerViewport>
        <MessageScrollerButton
          data-testid="button-scroll-to-latest"
          className="px-3 py-1.5 text-xs-500"
        >
          <span className="flex items-center gap-1.5">
            <ArrowDown className="w-3.5 h-3.5" />
            <span>Scroll to latest</span>
          </span>
        </MessageScrollerButton>
      </MessageScroller>
    </div>
  )
}
